// Studby — subscribe page. Card-required 3-day trial that auto-charges unless
// cancelled, a "skip trial & pay now" option, and a Stripe portal to manage/cancel.
import { useState } from "react";
import { Check, Loader2, Sparkles, Zap, Settings } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const COMMON_PERKS = [
  "Photo problem solver (step-by-step)",
  "Notes-to-quiz generator (photo or PDF)",
  "AI chat tutor with memory",
  "Progress dashboard",
];

const PLANS = [
  { key: "studly_basic", plan: "basic", name: "Basic", price: "€3.99", quota: "50 questions / month", highlight: false },
  { key: "studly_unlimited", plan: "unlimited", name: "Pro", price: "€12.99", quota: "250 questions / month", highlight: true },
];

export default function Subscribe() {
  const { user } = useAuth();
  const [loadingKey, setLoadingKey] = useState(null); // e.g. "studly_basic" or "studly_basic:skip"
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState("");

  const status = user?.subscription_status;
  const isActive = status === "active";
  const isTrialing = status === "trialing";
  const hasSub = isActive || isTrialing;
  const currentPlan = user?.plan;

  const checkout = async (lookupKey, skipTrial) => {
    setLoadingKey(lookupKey + (skipTrial ? ":skip" : ""));
    setError("");
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        lookup_key: lookupKey,
        skip_trial: skipTrial,
      });
      window.location.href = data.checkout_url; // Stripe hosted checkout
    } catch (err) {
      setError(formatApiError(err));
      setLoadingKey(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    setError("");
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiError(err));
      setPortalLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Choose your plan</h1>
        <p className="text-muted-foreground mt-2 max-w-md mx-auto" data-testid="billing-note">
          Start a 3-day free trial — you won't be charged until it ends, and you can cancel anytime.
          <span className="block mt-1 text-sm">The free trial is limited to <span className="font-semibold text-foreground">5 questions per day</span>.</span>
        </p>
      </div>

      {/* Existing subscribers: manage/cancel via Stripe portal */}
      {hasSub && (
        <div className="bg-secondary border border-border rounded-2xl p-5 mb-6 flex items-center justify-between gap-3" data-testid="manage-subscription">
          <p className="text-sm text-primary font-medium">
            {isTrialing ? "You're on a free trial" : `You're subscribed — ${currentPlan} plan`}
          </p>
          <Button variant="outline" className="rounded-full" onClick={openPortal} disabled={portalLoading} data-testid="manage-billing-button">
            {portalLoading
              ? (<><Loader2 size={16} className="mr-1.5 animate-spin" aria-hidden="true" /> Opening…</>)
              : (<><Settings size={15} className="mr-1.5" aria-hidden="true" /> Manage / cancel</>)}
          </Button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-5">
        {PLANS.map((plan) => {
          const isCurrent = hasSub && currentPlan === plan.plan;
          return (
            <div
              key={plan.key}
              data-testid={`plan-${plan.name.toLowerCase()}`}
              className={`relative rounded-3xl p-7 border ${
                plan.highlight ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"
              }`}
            >
              {plan.highlight && (
                <span className="absolute -top-3 left-7 bg-accent text-accent-foreground text-[11px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                  Best value
                </span>
              )}
              <div className="flex items-center gap-2">
                {plan.highlight
                  ? <Zap size={18} className="opacity-80" aria-hidden="true" />
                  : <Sparkles size={18} className="text-accent" aria-hidden="true" />}
                <h2 className="font-heading text-xl font-bold">{plan.name}</h2>
              </div>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-heading text-4xl font-extrabold">{plan.price}</span>
                <span className={`mb-1 text-sm ${plan.highlight ? "opacity-80" : "text-muted-foreground"}`}>/month</span>
              </div>
              <p className={`mt-1 text-sm font-medium ${plan.highlight ? "opacity-90" : "text-primary"}`}>{plan.quota}</p>

              <ul className="mt-5 space-y-2.5">
                {COMMON_PERKS.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm">
                    <Check size={17} className="mt-0.5 shrink-0 text-accent" aria-hidden="true" /> {p}
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <p className="mt-7 font-medium text-center" data-testid={`current-plan-${plan.name.toLowerCase()}`}>
                  Your current plan ✓
                </p>
              ) : hasSub ? (
                <p className={`mt-7 text-sm text-center ${plan.highlight ? "opacity-80" : "text-muted-foreground"}`} data-testid={`switch-note-${plan.name.toLowerCase()}`}>
                  Use “Manage / cancel” above to switch plans.
                </p>
              ) : (
                <div className="mt-7 space-y-2.5">
                  <Button
                    size="lg"
                    variant={plan.highlight ? "secondary" : "default"}
                    onClick={() => checkout(plan.key, false)}
                    disabled={loadingKey !== null}
                    className="rounded-full w-full font-semibold"
                    data-testid={`choose-${plan.name.toLowerCase()}-button`}
                  >
                    {loadingKey === plan.key
                      ? (<><Loader2 size={18} className="mr-1.5 animate-spin" aria-hidden="true" /> Redirecting…</>)
                      : "Start 3-day free trial"}
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => checkout(plan.key, true)}
                    disabled={loadingKey !== null}
                    className={`rounded-full w-full font-semibold ${
                      plan.highlight
                        ? "bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                        : ""
                    }`}
                    data-testid={`skip-trial-${plan.name.toLowerCase()}-button`}
                  >
                    {loadingKey === `${plan.key}:skip`
                      ? (<><Loader2 size={18} className="mr-1.5 animate-spin" aria-hidden="true" /> Redirecting…</>)
                      : "Skip trial & subscribe now"}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <p className="text-sm text-destructive mt-5 text-center" data-testid="checkout-error">{error}</p>}
      <p className="text-center text-xs text-muted-foreground mt-6">
        Secure checkout by Stripe — pay by card, <span className="font-medium text-foreground">Apple&nbsp;Pay</span> or <span className="font-medium text-foreground">Google&nbsp;Pay</span>.
        <br />Test card 4242 4242 4242 4242 · any future date · any CVC.
      </p>
    </div>
  );
}
